import { type IstanzaSezioneDatiDatiJson } from '@framework/api-client/api/backoffice/schemas/istanzaSezioneDatiDatiJson.ts';
import { type ValoriSezione } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezione.ts';
import { type ValoriSezioneValori } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezioneValori.ts';
import { set } from 'lodash';
import { type IEngineValidationContext } from '../IEngineValidationContext.ts';
import { type IContextSnapshot } from './IContextSnapshot.ts';
import { CONTEXT_SNAPSHOT_KEY } from './serializeContextSnapshot.ts';

/**
 * Mantiene il tipo di `IEngineValidationContext`, inizializzando tutte le proprietà minime.
 * I campi impostati saranno poi sovrascritti con i valori dello snapshot.
 */
function mapToEngineValidationContext(
  input: Record<string, any>,
): IEngineValidationContext {
  return {
    dossierSemanticFields: {},
    metadata: {},
    registers: {},
    semanticFields: {},
    session: {},
    externalServiceContext: null,
    trustedEnvironment: false,
    isIntegration: false,
    ...input,
  };
}

function getSnapshotValueFromSection(
  values: ValoriSezioneValori | IstanzaSezioneDatiDatiJson | null | undefined,
): IContextSnapshot | null {
  if (!values || typeof values !== 'object') {
    return null;
  }
  const snapshot = values[CONTEXT_SNAPSHOT_KEY];
  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }
  return snapshot as IContextSnapshot;
}

function deserialize(snapshot: IContextSnapshot): Record<string, any> {
  const deserialized: Record<string, any> = {};
  Object.keys(snapshot).forEach(path => {
    const value = snapshot[path];
    if (value !== undefined) {
      // Imposto il valore nell'oggetto deserializzato
      set(deserialized, path, value);
    }
  });

  return deserialized;
}

/**
 * Trasforma lo snapshot del contesto di validazione salvato e ritornato da BE
 * in un oggetto serializzato e con la struttura corretta per essere usato
 * nel validatore.
 *
 */
export function deserializeContextSnapshot(
  values: ValoriSezioneValori | IstanzaSezioneDatiDatiJson | null | undefined,
): IEngineValidationContext | null {
  // La struttura dello snapshto è { <path>: <value>, ... }
  // Bisogna ritornare un oggetto con la struttura corretta per essere usato nel validatore.
  // quindi se lo snapsho è "{ "foo.bar": "baz" }"
  // deve essere trasformato in { foo: { bar: "baz" } }
  const snapshot = getSnapshotValueFromSection(values);
  if (!snapshot) {
    return null;
  }

  const deserialized = deserialize(snapshot);
  return mapToEngineValidationContext(deserialized);
}

export function deserializeContextSnapshots(
  values: ValoriSezione[] | null | undefined,
): IEngineValidationContext | null {
  if (!values || !Array.isArray(values)) {
    return null;
  }

  // Crea un oggetto che è il merge di tutti gli snapshot estratti con "getSnapshotValueFromSection"
  const contextSnapshots = values
    .map(v => getSnapshotValueFromSection(v.valori))
    .filter((snapshot): snapshot is IContextSnapshot => snapshot !== null);

  if (contextSnapshots.length === 0) {
    return null;
  }

  // Unisco tutti gli snapshot in un unico oggetto
  const mergedSnapshot: IContextSnapshot = contextSnapshots.reduce(
    (acc, snapshot) => {
      return {
        ...acc,
        ...snapshot, // Unisco gli snapshot
      };
    },
    {} as IContextSnapshot,
  );

  // Calcolo il contesto di validazione a partire dallo snapshot unito
  const deserialized = deserialize(mergedSnapshot);

  if (!deserialized) return null;

  return mapToEngineValidationContext(deserialized);
}
